import {
  Autocomplete,
  Box,
  TextField,
  Typography,
  createFilterOptions,
  useTheme,
} from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import { useCreateRecipeMutation, useGetDentistQuery } from '@/store/apiService'

const filter = createFilterOptions<DentistOption>()

interface Dentist {
  id: string
  name: string
}

interface DentistOption {
  title: string
  id: string
  inputValue?: string
}

interface NewProjectProps {
  open: boolean
  onClose: () => void
  refetchRecipe: () => void | Promise<void>
}

function NewProject({ open, onClose, refetchRecipe }: NewProjectProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'project.newProjectDialog',
  })

  const [form, setForm] = useState({
    patientName: '',
    dentist: '',
    description: '',
  })

  const [dentistValue, setDentistValue] = useState<
    DentistOption | string | null
  >(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleDentistChange = (
    event: React.SyntheticEvent,
    newValue: DentistOption | string | null,
  ) => {
    if (typeof newValue === 'string') {
      setDentistValue(newValue)
      setForm((prev) => ({ ...prev, dentist: newValue }))
    } else if (newValue?.inputValue) {
      setDentistValue(newValue.inputValue)
      setForm((prev) => ({ ...prev, dentist: newValue.inputValue || '' }))
    } else {
      setDentistValue(newValue)
      setForm((prev) => ({ ...prev, dentist: newValue ? newValue.title : '' }))
    }
  }

  const isFormValid = Boolean(form.patientName)

  const [createRecipe] = useCreateRecipeMutation()

  const handleCreate = async () => {
    if (!isFormValid) return

    const payload: any = {
      patient_name: form.patientName.trim(),
      description: form.description.trim(),
      name: form.patientName.trim(),
    }

    if (typeof dentistValue === 'object' && dentistValue !== null) {
      payload.dentist = {
        id: dentistValue.id,
        name: dentistValue.id ? '' : dentistValue.title,
      }
    } else if (typeof dentistValue === 'string') {
      payload.dentist = {
        name: dentistValue.trim(),
      }
    }

    try {
      await createRecipe(payload).unwrap()
      await refetchRecipe()
      toast.success(t('createSuccess'))
      onClose()
    } catch (error: any) {
      const errorMessage = error?.data?.message || t('createFailed')
      toast.error(errorMessage)
    }
  }

  const theme = useTheme()

  const { data: dentistList } = useGetDentistQuery({})

  const dentistOptions: DentistOption[] = Array.isArray(dentistList)
    ? dentistList.map((item: Dentist) => ({
        title: item.name,
        id: item.id,
      }))
    : []

  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      title={
        <Typography fontSize={20} fontWeight={500} textAlign={'left'}>
          {t('title')}
        </Typography>
      }
      content={
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label={t('projectName')}
            name="patientName"
            value={form.patientName}
            onChange={handleChange}
            fullWidth
            variant="outlined"
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '40px',
                '& input': {
                  '&:-webkit-autofill': {
                    boxShadow: '0 0 0 1000px #292929 inset',
                    transition: 'background-color 5000s ease-in-out 0s',
                  },
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#5B5B5B',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#90CAF9',
                },
              },
              '& .MuiInputLabel-root': {
                top: '-8px',
              },
              '& .MuiInputLabel-shrink': {
                top: '0px',
              },
            }}
          />

          <Autocomplete
            value={dentistValue}
            onChange={handleDentistChange}
            filterOptions={(options, params) => {
              const filtered = filter(options, params)
              const { inputValue } = params

              if (
                inputValue !== '' &&
                !options.some((option) => option.title === inputValue)
              ) {
                filtered.push({
                  inputValue,
                  title: `Add "${inputValue}"`,
                  id: '',
                })
              }
              return filtered
            }}
            options={dentistOptions}
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                return option
              }
              if (option.inputValue) {
                return option.inputValue
              }
              return option.title
            }}
            renderOption={(props, option) => (
              <li {...props} key={`${option.id || 'new'}-${option.title}`}>
                {option.title}
              </li>
            )}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('dentistName')}
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#5B5B5B',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#90CAF9',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    top: '-8px',
                  },
                  '& .MuiInputLabel-shrink': {
                    top: '0px',
                  },
                }}
              />
            )}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'window',
                    },
                  },
                ],
              },
              paper: {
                sx: {
                  backgroundColor: theme.palette.background.disableTab,
                  fontSize: 16,
                  fontWeight: 400,
                  paddingY: '6px',
                },
              },
            }}
          />
          <TextField
            label={t('description')}
            name="description"
            value={form.description}
            onChange={handleChange}
            fullWidth
            variant="outlined"
            size="medium"
            multiline
            rows={4}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#5B5B5B',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#90CAF9',
                },
              },
            }}
          />
        </Box>
      }
      actionButtons={
        <Box width="100%" gap={2} display="flex" justifyContent="flex-end">
          <Button
            label={t('cancelButton')}
            onClick={onClose}
            variant="outlined"
            textTransform="uppercase"
          />
          <Button
            label={t('createButton')}
            onClick={handleCreate}
            variant="contained"
            disabled={!isFormValid}
            textTransform="uppercase"
          />
        </Box>
      }
      triggerElement={undefined}
      paperStyles={{ width: '800px' }}
    />
  )
}

export default NewProject
